import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  SwipeableDrawer,
  Tabs,
  Tab,
  Divider,
  Paper,
  CircularProgress,
  Modal,
  Backdrop,
} from "@mui/material";

// MUI Icons
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CodeIcon from "@mui/icons-material/Code";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SchoolIcon from "@mui/icons-material/School";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FeedbackIcon from "@mui/icons-material/Feedback";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";

// Import images
import p1Img1 from "images/faq/demo-project/p1_01.png";
import p1Img2 from "images/faq/demo-project/p1_02.png";
import p1Img3 from "images/faq/demo-project/p1_03.png";
import p1Img4 from "images/faq/demo-project/p1_04.png";
import p2Img1 from "images/faq/demo-project/p2_01.png";
import p2Img2 from "images/faq/demo-project/p2_02.png";
import p2Img3 from "images/faq/demo-project/p2_03.png";
import BgTitle from "components/title/BgTitle";

// Styled Components - remain the same
const AppContainer = styled(Box)`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
`;

const StyledTabs = styled(Tabs)`
  background-color: white;
  border-radius: 20px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: -25px;
  position: relative;
  z-index: 10;

  & .MuiTab-root {
    min-height: 45px;
    border-radius: 16px;
    font-size: 0.7rem;
    font-weight: 500;
    padding: 6px 8px;
    min-width: 0;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    @media (max-width: 400px) {
      font-size: 0.65rem;
      padding: 6px 6px;
    }
  }

  & .Mui-selected {
    background-color: #e6f2ff;
    color: #0c4d96;
  }

  & .MuiTabs-indicator {
    display: none;
  }
`;

const CategoryCard = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  cursor: pointer;
`;

const CategoryIcon = styled(Avatar)`
  background-color: #e6f2ff;
  color: #0c4d96;
  margin-right: 12px;
`;

const ImageGallery = styled(Box)`
  position: relative;
  height: 240px;
  margin-top: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageIndicator = styled(Box)`
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 16px;
  width: 100%;
  z-index: 5;
`;

const ImageDot = styled(Box)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? "#0c4d96" : "rgba(255, 255, 255, 0.7)"};
  margin: 0 4px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const NavButtonWrapper = styled(Box)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  left: ${(props) => (props.position === "left" ? "8px" : "auto")};
  right: ${(props) => (props.position === "right" ? "8px" : "auto")};
`;

const NavButton = styled(IconButton)`
  background-color: rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 32px;
  height: 32px;
  &:hover {
    background-color: rgba(255, 255, 255, 0.95) !important;
  }
`;

const DetailCard = styled(Paper)`
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  position: relative;
  margin-bottom: 16px;
`;

const TimelineItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 16px;
    top: 32px;
    bottom: -12px;
    width: 2px;
    background-color: ${(props) => (props.isLast ? "transparent" : "#e5e7eb")};
  }
`;

const TimelineCircle = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #dbeafe;
  color: #0c4d96;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  margin-right: 12px;
  z-index: 2;
`;

const ProgressBar = styled(Box)`
  height: 8px;
  border-radius: 4px;
  background-color: #e5e7eb;
  width: 100%;
  position: relative;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressIndicator = styled(Box)`
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #3b82f6 0%, #93c5fd 100%);
  width: ${(props) => props.value}%;
  transition: width 0.8s ease;
`;

// New components for full-screen image modal
const FullscreenImage = styled(Box)`
  position: relative;
  height: 90vh;
  width: 90vw;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ImageHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e5e7eb;
`;

const ImageContent = styled(Box)`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  padding: 12px;
`;

const ZoomIconOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 15;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

// Format multiline text
const formatMultilineText = (text) => {
  if (!text) return "";
  return text.split("/n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < text.split("/n").length - 1 && <br />}
    </React.Fragment>
  ));
};

// Main Component
const RecruitmentProcessDetailsMobileView = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingState(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset states
    setCurrentImage(0);
    setAnimationComplete(false);

    // Simulate data loading when changing tabs
    setLoadingState(true);
    setTimeout(() => {
      setLoadingState(false);
    }, 600);
  };

  const handleDrawerOpen = (category) => {
    setSelectedCategory(category);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const nextImage = () => {
    const totalImages = currentApp.images.length;
    setCurrentImage((prev) => (prev + 1) % totalImages);
  };

  const prevImage = () => {
    const totalImages = currentApp.images.length;
    setCurrentImage((prev) => (prev - 1 + totalImages) % totalImages);
  };

  // For fullscreen modal
  const handleOpenImage = (index) => {
    setSelectedImageIndex(index);
    setOpenImageModal(true);
  };

  const handleCloseImage = () => {
    setOpenImageModal(false);
  };

  const nextModalImage = () => {
    const totalImages = currentApp.images.length;
    setSelectedImageIndex((prev) => (prev + 1) % totalImages);
  };

  const prevModalImage = () => {
    const totalImages = currentApp.images.length;
    setSelectedImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  // Get the current app data from translations
  const getBirdHospitalApp = () => {
    const appData = t("recruitment-process-content.bird-hospital-app", {
      returnObjects: true,
    });
    const categories = t(
      "recruitment-process-content.bird-hospital-app.categories",
      { returnObjects: true }
    );

    return {
      title: appData.title,
      author: appData.author,
      year: appData.year,
      description: categories.reason.content,
      schedule: {
        total: categories.schedule.total_days,
        steps: categories.schedule.steps,
      },
      impression: categories.impression.content,
      skills: categories.skills.content,
      advice: categories.advice.content,
      images: appData.images.map((img, index) => ({
        src: [p1Img1, p1Img2, p1Img3, p1Img4][index],
        alt: img.alt,
        caption: img.caption,
      })),
    };
  };

  const getChecklistApp = () => {
    const appData = t("recruitment-process-content.checklist-app", {
      returnObjects: true,
    });
    const categories = t(
      "recruitment-process-content.checklist-app.categories",
      { returnObjects: true }
    );

    return {
      title: appData.title,
      author: appData.author,
      year: appData.year,
      description: categories.reason.content,
      schedule: {
        total: categories.schedule.total_days,
        steps: categories.schedule.steps,
      },
      impression: categories.impression.content,
      skills: categories.skills.content,
      advice: categories.advice.content,
      images: appData.images.map((img, index) => ({
        src: [p2Img1, p2Img2, p2Img3][index],
        alt: img.alt,
        caption: img.caption,
      })),
    };
  };

  const birdHospitalApp = getBirdHospitalApp();
  const checklistApp = getChecklistApp();

  const currentApp = tabValue === 0 ? birdHospitalApp : checklistApp;

  // Category data with translations
  const getCategoryData = () => {
    const appType = tabValue === 0 ? "bird-hospital-app" : "checklist-app";
    const categories = t(`recruitment-process-content.${appType}.categories`, {
      returnObjects: true,
    });

    return [
      {
        id: "reason",
        title: categories.reason.title,
        icon: <InfoOutlinedIcon />,
        content: currentApp.description,
      },
      {
        id: "schedule",
        title: categories.schedule.title,
        icon: <CalendarMonthIcon />,
        content: currentApp.schedule,
      },
      {
        id: "impression",
        title: categories.impression.title,
        icon: <FeedbackIcon />,
        content: currentApp.impression,
      },
      {
        id: "skills",
        title: categories.skills.title,
        icon: <SchoolIcon />,
        content: currentApp.skills,
      },
      {
        id: "advice",
        title: categories.advice.title,
        icon: <LightbulbIcon />,
        content: currentApp.advice,
      },
    ];
  };

  const categories = getCategoryData();

  // Render drawer content based on selected category
  const renderDrawerContent = () => {
    if (!selectedCategory) return null;
    const appType = tabValue === 0 ? "bird-hospital-app" : "checklist-app";
    const categoryTranslations = t(
      `recruitment-process-content.${appType}.categories`,
      { returnObjects: true }
    );

    switch (selectedCategory.id) {
      case "reason":
        return (
          <Box p={3}>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              {formatMultilineText(selectedCategory.content)}
            </Typography>
          </Box>
        );
      case "schedule":
        return (
          <Box p={3}>
            <Box
              mb={3}
              sx={{
                backgroundColor: "#f0f7ff",
                p: 2,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <AccessTimeIcon sx={{ mr: 2, color: "#3b82f6" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {categoryTranslations.schedule.development_period}
              </Typography>
            </Box>

            {selectedCategory.content.steps.map((step, index) => (
              <TimelineItem
                key={index}
                isLast={index === selectedCategory.content.steps.length - 1}
              >
                <TimelineCircle>{index + 1}</TimelineCircle>
                <Box flex={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight={500}>
                      {step.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.days}日
                    </Typography>
                  </Box>
                  <ProgressBar>
                    <ProgressIndicator
                      value={(step.days / selectedCategory.content.total) * 100}
                    />
                  </ProgressBar>
                </Box>
              </TimelineItem>
            ))}
          </Box>
        );
      case "impression":
        return (
          <Box p={3}>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              {formatMultilineText(selectedCategory.content)}
            </Typography>
          </Box>
        );
      case "skills":
        return (
          <Box p={3}>
            <DetailCard elevation={0}>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {selectedCategory.content}
              </Typography>
            </DetailCard>
          </Box>
        );
      case "advice":
        return (
          <Box p={3}>
            <DetailCard elevation={0}>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {selectedCategory.content}
              </Typography>
            </DetailCard>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <AppContainer sx={{ mt: 2 }}>
      <BgTitle
        text={t("recruitment-process-content.training-programs.title")}
        isCenter={true}
      />
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "left",
          mt: 2,
          color: "#64748b",
          maxWidth: "800px",
          mx: "auto",
          fontSize: "0.85rem",
        }}
      >
        {t("recruitment-process-content.additional_msg_subtitle")}
        <br />
        {t("recruitment-process-content.additional_msg_description")}
      </Typography>
      <Box sx={{ mt: 6 }}>
        <StyledTabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab
            icon={<CodeIcon fontSize="small" />}
            label={t(
              "recruitment-process-content.training-programs.tabs.mobile-bird-hospital"
            )}
            iconPosition="start"
          />
          <Tab
            icon={<PersonOutlineIcon fontSize="small" />}
            label={t(
              "recruitment-process-content.training-programs.tabs.mobile-checklist"
            )}
            iconPosition="start"
          />
        </StyledTabs>
      </Box>

      <Box px={2} py={3}>
        {loadingState ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress size={40} sx={{ color: "#0c4d96" }} />
          </Box>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={tabValue}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              onAnimationComplete={() => setAnimationComplete(true)}
            >
              <DetailCard>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: "#e6f2ff",
                      color: "#0c4d96",
                      mr: 2,
                    }}
                  >
                    <CodeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="#0c4d96">
                      {currentApp.title}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mr={1}
                      >
                        {t("recruitment-process-content.common.creator")}{" "}
                        {currentApp.author}
                      </Typography>
                      <Chip
                        label={`${currentApp.year}${t(
                          "recruitment-process-content.common.hire_year"
                        )}`}
                        size="small"
                        sx={{
                          height: "20px",
                          fontSize: "0.6rem",
                          backgroundColor: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <ImageGallery>
                  <AnimatePresence initial={false} mode="wait">
                    <motion.div
                      key={currentImage}
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={currentApp.images[currentImage].src}
                        alt={currentApp.images[currentImage].alt}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "180px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          mb: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => handleOpenImage(currentImage)}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                      >
                        {currentApp.images[currentImage].caption}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>

                  {/* Zoom icon overlay */}
                  <ZoomIconOverlay
                    onClick={() => handleOpenImage(currentImage)}
                  >
                    <ZoomOutMapIcon
                      sx={{ color: "#0c4d96", fontSize: "1.2rem" }}
                    />
                  </ZoomIconOverlay>

                  {/* Fixed navigation buttons */}
                  <NavButtonWrapper position="left">
                    <NavButton size="small" onClick={prevImage}>
                      <ChevronLeftIcon fontSize="small" />
                    </NavButton>
                  </NavButtonWrapper>

                  <NavButtonWrapper position="right">
                    <NavButton size="small" onClick={nextImage}>
                      <ChevronRightIcon fontSize="small" />
                    </NavButton>
                  </NavButtonWrapper>

                  <ImageIndicator>
                    {currentApp.images.map((_, index) => (
                      <ImageDot key={index} active={index === currentImage} />
                    ))}
                  </ImageIndicator>
                </ImageGallery>
              </DetailCard>

              <AnimatePresence>
                {animationComplete && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <Typography variant="h6" fontWeight={600} mb={2} px={1}>
                      {t(
                        "recruitment-process-content.training-programs.program-info"
                      )}
                    </Typography>

                    {categories.map((category, index) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 * index }}
                      >
                        <CategoryCard
                          onClick={() => handleDrawerOpen(category)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <CategoryIcon>{category.icon}</CategoryIcon>
                          <Typography variant="subtitle1" fontWeight={500}>
                            {category.title}
                          </Typography>
                          <Box sx={{ flexGrow: 1 }} />
                          <ChevronRightIcon color="action" />
                        </CategoryCard>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        )}
      </Box>

      {/* Category Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleDrawerClose}
        onOpen={() => {}}
        disableSwipeToOpen={true}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            maxHeight: "80vh",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          p={2}
          borderBottom="1px solid #e5e7eb"
        >
          <IconButton edge="start" onClick={handleDrawerClose}>
            <CloseIcon />
          </IconButton>
          {selectedCategory && (
            <>
              <CategoryIcon sx={{ mx: 1.5 }}>
                {selectedCategory.icon}
              </CategoryIcon>
              <Typography variant="subtitle1" fontWeight={600}>
                {selectedCategory.title}
              </Typography>
            </>
          )}
        </Box>
        {renderDrawerContent()}
      </SwipeableDrawer>

      {/* Fullscreen Image Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImage}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          style={{ width: "100%", maxWidth: "100vw" }}
        >
          <FullscreenImage>
            <ImageHeader>
              <Typography
                variant="body1"
                fontWeight={600}
                noWrap
                sx={{ flex: 1 }}
              >
                {currentApp.images[selectedImageIndex].alt}
              </Typography>
              <IconButton
                onClick={handleCloseImage}
                size="medium"
                sx={{
                  color: "#64748b",
                  "&:hover": { color: "#0c4d96" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </ImageHeader>
            <ImageContent>
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={selectedImageIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={currentApp.images[selectedImageIndex].src}
                    alt={currentApp.images[selectedImageIndex].alt}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "calc(90vh - 120px)",
                      objectFit: "contain",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Navigation buttons in modal */}
              <NavButtonWrapper position="left">
                <NavButton size="small" onClick={prevModalImage}>
                  <ChevronLeftIcon />
                </NavButton>
              </NavButtonWrapper>

              <NavButtonWrapper position="right">
                <NavButton size="small" onClick={nextModalImage}>
                  <ChevronRightIcon />
                </NavButton>
              </NavButtonWrapper>
            </ImageContent>
            <Box sx={{ p: 3, borderTop: "1px solid #e5e7eb" }}>
              <Typography variant="body2" color="text.secondary">
                {currentApp.images[selectedImageIndex].caption}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                {currentApp.images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor:
                        index === selectedImageIndex ? "#0c4d96" : "#e5e7eb",
                      margin: "0 4px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setSelectedImageIndex(index)}
                  />
                ))}
              </Box>
            </Box>
          </FullscreenImage>
        </motion.div>
      </Modal>
    </AppContainer>
  );
};

export default RecruitmentProcessDetailsMobileView;
